import { collect, iterate } from "../../graph/paging.ts";
import { MODULE_SCOPES } from "../../auth/config.ts";
import { InteractionRequiredError, NotSignedInError } from "../../auth/broker.ts";
import { GraphError, graphFetch } from "../../graph/client.ts";
import { writeFile } from "../../storage/fsa.ts";
import { getCursor, putCursor, isItemDone, putItem } from "../../storage/idb.ts";
import type { RunLogger } from "../../export/run.ts";
import { ExportAbortedError, throwIfAborted } from "../../export/abort.ts";
import {
    withinRange,
    isContainerSelected,
    walkScope,
    scopeCovers,
    type DateRange,
    type ContainerSelection
} from "../../export/plan.ts";
import { resolveFolderPaths, messageFilename, messagePath } from "./paths.ts";
import { renderFolderIndex, renderMailIndex, type MailFolderEntries } from "./render.ts";
import type { MailFolder, MessageDelta } from "./types.ts";

/**
 * Mail export (ARCHITECTURE.md §15 step 6).
 *
 * Structurally close to Teams, with one real simplification: mail folders
 * support `/delta` (§8), so — unlike Teams chats, which needed a bespoke
 * lastUpdatedDateTime check to notice new activity in a "done" chat — a
 * repeat run is just resuming from the stored deltaLink. Graph's delta
 * protocol *is* the "what changed since last time" answer; there is no
 * separate freshness check to invent.
 *
 * $value returns the complete raw MIME for a message, attachments included —
 * so unlike Teams there is no separate asset-fetching pass for this module at
 * all: one Graph request, one file, done.
 */

const SCOPES = MODULE_SCOPES.mail;

export interface MailProgress {
    foldersTotal: number;
    foldersDone: number;
    currentFolderLabel?: string;
    /** Bytes written for the folder currently being fetched. */
    currentFolderBytes: number;
    /** Bytes written across this whole invocation. */
    totalBytes: number;
}

export type MailProgressCallback = (progress: MailProgress) => void;

/** One finished folder pass, for the console's scrollback. */
export interface MailFolderSummary {
    label: string;
    /** Messages fetched and written *this call* — 0 if nothing was new. */
    messages: number;
    bytes: number;
}

export interface ExportMailParams {
    runId: string;
    root: FileSystemDirectoryHandle;
    /** The signed-in user's own address — the `<mailbox>` path segment (§7). */
    mailboxLabel: string;
    /** From the Scan step: only export these folders (undefined/"all" = every folder). */
    selection?: ContainerSelection;
    /** From the Scan step: skip messages received outside this date window. */
    dateRange?: DateRange;
    onProgress?: MailProgressCallback;
    onFolderComplete?: (summary: MailFolderSummary) => void;
    /** Aborts at the next clean checkpoint. */
    signal?: AbortSignal;
    logger: RunLogger;
}

export interface MailResult {
    foldersTotal: number;
    messages: number;
    bytes: number;
    errors: number;
}

/**
 * Fetches and writes every not-yet-done message in one folder, checkpointing
 * the delta cursor at each page boundary — an interruption at any point costs
 * at most the one message in flight, and a later run resumes from the same
 * deltaLink/nextLink rather than re-listing the folder from scratch.
 */
async function exportFolderMessages(params: {
    runId: string;
    root: FileSystemDirectoryHandle;
    folder: MailFolder;
    mailboxLabel: string;
    folderPath: readonly string[];
    dateRange?: DateRange;
    logger: RunLogger;
    signal?: AbortSignal;
    onBytes?: (bytes: number) => void;
}): Promise<{ messages: number; bytes: number }> {
    const { runId, root, folder, mailboxLabel, folderPath, dateRange, logger, signal, onBytes } = params;

    const scope = walkScope(dateRange);
    const stored = await getCursor(runId, "mail", folder.id);
    // A cursor walked under a narrower date window moved past whatever that
    // window skipped; continuing from it would never list those items again,
    // so a wider window re-lists the container (done items are still skipped).
    const cursor = stored && scopeCovers(stored.scope, scope) ? stored : undefined;
    // deltaLink (a completed pass, ready for an incremental check) takes
    // priority over a stray nextLink from an interrupted earlier pass — once
    // a deltaLink exists, that *is* the correct resume point regardless of
    // what done/nextLink happened to be left at.
    const startUrl =
        cursor?.deltaLink ??
        cursor?.nextLink ??
        `/me/mailFolders/${folder.id}/messages/delta?$select=receivedDateTime`;

    let messages = 0;
    let bytes = 0;

    for await (const entry of iterate<MessageDelta>(startUrl, {
        scopes: SCOPES,
        service: "outlook",
        ...(signal ? { signal } : {}),
        onDeltaLink: (deltaLink) => {
            void putCursor({ runId, module: "mail", subKey: folder.id, deltaLink, done: true, scope });
        },
        onNextLink: (nextLink) => {
            void putCursor({ runId, module: "mail", subKey: folder.id, nextLink, done: false, scope });
        }
    })) {
        throwIfAborted(signal);

        if (entry["@removed"]) {
            // A message deleted or moved out of this folder since the last
            // pass. Not fetched — and not deleted from disk either: this is a
            // backup, and its job is to preserve what was already captured,
            // not mirror a deletion made after the fact.
            continue;
        }

        const itemId = entry.id;
        // Deliberately id-only, unlike the other modules' itemNeedsExport():
        // a message's MIME never changes, while delta also reports read/flag
        // changes — re-downloading the .eml for those would be pure waste.
        if (await isItemDone(runId, "mail", itemId)) {
            continue;
        }

        // Date filter (Scan step): receivedDateTime comes from the delta's
        // $select, so this costs no extra request — the $value fetch below is
        // simply skipped for out-of-window messages.
        if (dateRange && !withinRange(entry.receivedDateTime, dateRange)) {
            continue;
        }

        let response: Response;
        try {
            response = await graphFetch(`/me/messages/${entry.id}/$value`, {
                scopes: SCOPES,
                service: "outlook",
                ...(signal ? { signal } : {})
            });
        } catch (err) {
            if (err instanceof GraphError && err.status === 404) {
                // Listed, then deleted before we could fetch it. Recorded so
                // it is not retried forever, not treated as a folder failure.
                await logger.info(`message vanished before fetch (404), skipped: ${itemId}`);
                await putItem({ runId, module: "mail", itemId, status: "skipped", bytes: 0 });
                continue;
            }
            throw err;
        }

        const blob = await response.blob();
        const filename = await messageFilename(entry.id, entry.receivedDateTime);
        const path = messagePath(mailboxLabel, folderPath, filename);
        const written = await writeFile(root, path, blob);
        await putItem({ runId, module: "mail", itemId, status: "done", bytes: written });

        messages += 1;
        bytes += written;
        onBytes?.(written);
    }

    return { messages, bytes };
}

export async function exportMail(params: ExportMailParams): Promise<MailResult> {
    const { runId, root, mailboxLabel, selection, dateRange, onProgress, onFolderComplete, signal, logger } =
        params;

    await logger.info("listing folders");
    const allFolders = await collect<MailFolder>("/me/mailFolders/delta", {
        scopes: SCOPES,
        service: "outlook",
        ...(signal ? { signal } : {})
    });
    // Scan-step selection: only the chosen folders.
    const folders = allFolders.filter((f) => isContainerSelected(selection, f.id));
    await logger.info(`found ${allFolders.length} folders, ${folders.length} selected`);

    // Resolve paths from the full set so a selected sub-folder still gets its
    // parent segments even when the parent itself wasn't selected.
    const paths = resolveFolderPaths(allFolders);

    let totalBytes = 0;
    let totalMessages = 0;
    let errors = 0;
    const folderEntries: MailFolderEntries[] = [];

    for (let i = 0; i < folders.length; i += 1) {
        throwIfAborted(signal);

        const folder = folders[i];
        if (!folder) {
            continue;
        }

        const folderPath = paths.get(folder.id) ?? [folder.displayName];
        const label = folderPath.join("/");
        let folderBytes = 0;

        onProgress?.({
            foldersTotal: folders.length,
            foldersDone: i,
            currentFolderLabel: label,
            currentFolderBytes: 0,
            totalBytes
        });

        try {
            const result = await exportFolderMessages({
                runId,
                root,
                folder,
                mailboxLabel,
                folderPath,
                ...(dateRange ? { dateRange } : {}),
                logger,
                ...(signal ? { signal } : {}),
                onBytes: (chunk) => {
                    folderBytes += chunk;
                    totalBytes += chunk;
                    onProgress?.({
                        foldersTotal: folders.length,
                        foldersDone: i,
                        currentFolderLabel: label,
                        currentFolderBytes: folderBytes,
                        totalBytes
                    });
                }
            });

            totalMessages += result.messages;
            onFolderComplete?.({ label, messages: result.messages, bytes: result.bytes });
        } catch (err) {
            if (err instanceof ExportAbortedError) {
                throw err;
            }
            // Auth expiry affects every subsequent request, not just this
            // folder — halt immediately rather than generating the same
            // failure per folder. Anything else (a folder-specific error the
            // client's own retries already exhausted) is this folder's
            // problem alone: log it, leave its cursor untouched so a future
            // resume retries it, and keep going.
            if (err instanceof InteractionRequiredError || err instanceof NotSignedInError) {
                throw err;
            }
            errors += 1;
            const message = err instanceof GraphError ? err.toLogString() : String(err);
            await logger.error(`folder failed, will retry on resume: ${message}`);
            onFolderComplete?.({ label, messages: 0, bytes: 0 });
        }

        // Idempotent and network-free — safe whether this folder just
        // finished, had nothing new, or failed above and is only partially
        // fetched so far.
        const entries = await renderFolderIndex(root, mailboxLabel, folderPath);
        folderEntries.push({ folderPath, entries });
    }

    // Idempotent and network-free, like each folder's own _index.json above.
    await renderMailIndex(root, mailboxLabel, folderEntries);

    onProgress?.({
        foldersTotal: folders.length,
        foldersDone: folders.length,
        currentFolderBytes: 0,
        totalBytes
    });
    await logger.info(`mail export complete: ${totalMessages} messages, ${errors} errors`);

    return { foldersTotal: folders.length, messages: totalMessages, bytes: totalBytes, errors };
}
